import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useActiveJourneyMapDraft } from '../../../../hooks/journey-map-hooks';
import JourneyMapSidePanelItemEditor from './JourneyMapSidePanelItemEditor';
import JourneyMapSidePanelLinkEditor from './JourneyMapSidePanelLinkEditor';
import JourneyMapSidePanelPathGroupEditor from './JourneyMapSidePanelPathGroupEditor';
import JourneyMapSidePanelMapSettings from './JourneyMapSidePanelMapSettings';
import { loadProviderItems } from './journeyMapSidePanelUtils';
import { getContentRefTypeOption } from '../../../../support/journeyMap/contentSources';

export default function JourneyMapSidePanel() {
  const draft = useActiveJourneyMapDraft();

  const [providerItems, setProviderItems] = useState<any[]>([]);
  const [providerItemsLoading, setProviderItemsLoading] = useState(false);

  const selectedItem = useMemo(() => {
    if (!draft?.selectedItemTempId) return null;
    return (
      draft.items.find(
        (item) => item.tempId === draft.selectedItemTempId && !item.isDeleted,
      ) || null
    );
  }, [draft]);

  const selectedLink = useMemo(() => {
    if (!draft?.selectedLinkTempId) return null;
    return (
      draft.links.find(
        (link) => link.tempId === draft.selectedLinkTempId && !link.isDeleted,
      ) || null
    );
  }, [draft]);

  const selectedPathGroup = useMemo(() => {
    if (!draft?.selectedPathGroupTempId) return null;
    return (
      draft.pathGroups.find(
        (group) => group.tempId === draft.selectedPathGroupTempId && !group.isDeleted,
      ) || null
    );
  }, [draft]);

  const availableItems = useMemo(() => {
    return draft?.items.filter((item) => !item.isDeleted) || [];
  }, [draft]);

  const selectedContentRefTypeOption = useMemo(() => {
    if (!selectedItem?.content_ref_type) return null;
    return getContentRefTypeOption(selectedItem.content_ref_type);
  }, [selectedItem?.content_ref_type]);

  useEffect(() => {
    let active = true;

    const run = async () => {
      const endpoint = selectedContentRefTypeOption?.endpoint || '';

      if (!endpoint) {
        if (active) {
          setProviderItems([]);
          setProviderItemsLoading(false);
        }
        return;
      }

      if (active) setProviderItemsLoading(true);

      const items = await loadProviderItems(endpoint);

      if (active) {
        setProviderItems(items);
        setProviderItemsLoading(false);
      }
    };

    run();

    return () => {
      active = false;
    };
  }, [selectedContentRefTypeOption]);

  if (!draft) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No active draft selected.
        </Typography>
      </Box>
    );
  }

  if (selectedItem) {
    return (
      <JourneyMapSidePanelItemEditor
        item={selectedItem}
        providerItems={providerItems}
        providerItemsLoading={providerItemsLoading}
      />
    );
  }

  if (selectedLink) {
    return <JourneyMapSidePanelLinkEditor link={selectedLink} />;
  }

  if (selectedPathGroup) {
    return (
      <JourneyMapSidePanelPathGroupEditor
        pathGroup={selectedPathGroup}
        availableItems={availableItems}
      />
    );
  }

  return <JourneyMapSidePanelMapSettings draft={draft} />;
}